import { useCallback, useMemo } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { SteeringWheelSvg } from '~/components/image-selection-tile/components/fallback-svgs/steering-wheel-svg';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { OptionsHeader } from '../options-header';

export function SteeringWheels() {
	const { i18n } = useI18n();
	const { steeringWheels } = useCatalog();
	const { isLoading, loadingFlags, changeConfiguration, isItemLoading, isItemChecked } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { feature } = useVATConfig();
	const { config } = useMarketConfig();
	const { getName } = useDebugMode();
	const { mediaByProfileId } = useImages();
	const { step, total } = useSteps('steeringWheels');

	const trackSelectionClickEvent = useCallback(
		(eventAction: EventAction, item: FeatureOrPackage, selected: boolean) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'image',
				value: `selection feature app: steeringWheels: all`,
				category: 'steeringWheels',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const items = useMemo(() => {
		if (!steeringWheels) return [];

		// Map items for the tile selection
		const mappedItems = steeringWheels.map((wheel) => ({
			name: getName(wheel) ?? ' ',
			key: wheel.featureId,
			onSelect: () => {
				trackSelectionClickEvent(EventAction.InternalLink, wheel.__item, isItemChecked(wheel));
				changeConfiguration(wheel as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(wheel),
			readonly: !wheel.status.changeable,
			teaserText: wheel.__item.texts?.intro || '',
			standard: wheel.status.standard,
			imgUrl: wheel.stearingWheelImageSmallUrl,
			selected: isItemChecked(wheel),
			price: config.showPrices
				? { totalPrice: wheel.totalPrice, monthlyRate: wheel.monthlyRate, feature }
				: undefined,
			textLink: {
				label: i18n('ngc.actions.moreInfo'),
				onTextLinkClick: () =>
					openMoreInformationDialog(
						wheel as SelectableOption<FeatureCategory>,
						wheel.__item,
						() => {},
						step,
						total,
						'steeringWheels',
					),
			},
			disclaimerContent: wheel.__item.texts?.disclaimerText || undefined,
			disclaimerHeadline: wheel.__item.texts?.disclaimerHeadline || undefined,
			disclaimerStartDate: wheel.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: wheel.__item.texts?.disclaimerEndDate || undefined,
		}));

		return mappedItems;
	}, [
		JSON.stringify(steeringWheels),
		changeConfiguration,
		config.showPrices,
		getName,
		isItemChecked,
		isItemLoading,
		JSON.stringify(feature),
		trackSelectionClickEvent,
	]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'steeringWheels',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'steeringWheels')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			media={[[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]]]}
			id={'steeringWheels'}
			galleryControls={{ compare: true, avp: true }}
			headline={<OptionsHeader title={i18n('ngc.equipment.steeringWheels')} step={step} total={total} />}
			disclaimer={categoryDisclaimer}>
			<TilesSelection
				id="steeringWheels"
				items={items}
				fallbackSVG={SteeringWheelSvg}
				isLoading={loadingFlags.checkingConflicts}
			/>
		</ConfigurationStep>
	);
}
